import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import type { ScheduleEntry } from '@dosfilos/domain';
import { useTenantStore } from '../../../stores/tenant.store';
import { useAcademic } from '../../../hooks/use-academic';
import { errorText } from '../../../lib/error-text';
import { SectionScheduleEditor, scheduleDraftProblem } from './SectionScheduleEditor';

interface CreateSectionDialogProps {
  open: boolean;
  onClose: () => void;
  programs: any[];
  courses: any[];
  terms: any[];
  faculty: any[];
}

/** Course ids the program declares in any version of its curriculum. That set
 *  is what the program offers — see `coursesInCurriculum` in the domain, which
 *  the server validates the submission against. */
function plannedCourseIds(program: any): Set<string> {
  const ids = new Set<string>();
  for (const version of program?.['curriculumVersions'] ?? []) {
    for (const term of version?.terms ?? []) {
      for (const id of term?.recommendedCourseIds ?? []) ids.add(id as string);
    }
  }
  return ids;
}

export function CreateSectionDialog({
  open,
  onClose,
  programs,
  courses,
  terms,
  faculty,
}: CreateSectionDialogProps) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const { createSection, isCreatingSection } = useAcademic();

  const [programId, setProgramId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [termId, setTermId] = useState('');
  const [facultyId, setFacultyId] = useState('');
  const [maxEnrollment, setMaxEnrollment] = useState<number | ''>('');
  // «Sin límite de cupo» = cupo 0 (H7): la regla que el servidor y la
  // inscripción ya leen como sin límite (`sectionCapacity`).
  const [unlimited, setUnlimited] = useState(false);
  const [sectionCode, setSectionCode] = useState('');
  const [schedule, setSchedule] = useState<ScheduleEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The offer is the program's curriculum. This screen used to list "every
  // course in the catalog" under whichever program had been picked, which is how
  // production ended up with a course of one program taught under another.
  const { visibleCourses, programHasPlan } = useMemo(() => {
    const program = programs.find((p) => p.id === programId);
    const planned = plannedCourseIds(program);
    // No plan loaded = the program cannot answer, so nothing is hidden. Same
    // rule the server applies, and it must be the same one or the form would
    // offer what the callable then refuses.
    if (planned.size === 0) return { visibleCourses: courses, programHasPlan: false };
    return {
      visibleCourses: courses.filter((c) => planned.has(c.id)),
      programHasPlan: true,
    };
  }, [programs, programId, courses]);

  // When program changes, clear the selected course to force a fresh pick
  const handleProgramChange = (newProgramId: string) => {
    setProgramId(newProgramId);
    setCourseId('');
  };

  const handleClose = () => {
    // Reset all fields on close
    setProgramId('');
    setCourseId('');
    setTermId('');
    setFacultyId('');
    setMaxEnrollment('');
    setUnlimited(false);
    setSectionCode('');
    setSchedule(null);
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTenantId) return;

    setError(null);
    try {
      const payload: any = {
        tenantId: currentTenantId,
        programId,
        courseId,
        termId,
      };
      if (facultyId) payload.facultyId = facultyId;
      payload.maxEnrollment = unlimited ? 0 : Number(maxEnrollment);
      if (sectionCode.trim()) payload.sectionCode = sectionCode.trim();
      if (schedule && schedule.length > 0) payload.schedule = schedule;

      await createSection(payload);
      handleClose();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const capacityValid = unlimited || (maxEnrollment !== '' && Number(maxEnrollment) >= 1);
  const scheduleValid = !schedule || schedule.length === 0 || !scheduleDraftProblem(schedule);
  const isFormValid =
    !!programId && !!courseId && !!termId && !!facultyId && capacityValid && scheduleValid;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('sections.createDialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* ── 1. Programa (seleccionar primero) ── */}
            <TextField
              select
              label={t('sections.createDialog.program')}
              required
              fullWidth
              value={programId}
              onChange={(e) => handleProgramChange(e.target.value)}
              helperText={t('sections.createDialog.programHelper')}
            >
              <MenuItem value="" disabled>
                {t('sections.createDialog.programPlaceholder')}
              </MenuItem>
              {programs.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} ({p.code})
                </MenuItem>
              ))}
            </TextField>

            {/* ── 2. Curso (los que el programa declara en su malla) ── */}
            <TextField
              select
              label={t('sections.createDialog.course')}
              required
              fullWidth
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
              disabled={!programId}
              helperText={
                !programId
                  ? t('sections.createDialog.courseHelperNoProgram')
                  : programHasPlan
                    ? t('sections.createDialog.courseHelperFromCurriculum')
                    : t('sections.createDialog.courseHelperNoCurriculum')
              }
            >
              <MenuItem value="" disabled>
                {t('sections.createDialog.coursePlaceholder')}
              </MenuItem>
              {visibleCourses.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name} ({c.code})
                </MenuItem>
              ))}
            </TextField>

            {/* ── 3. Período académico ── */}
            <TextField
              select
              label={t('sections.createDialog.term')}
              required
              fullWidth
              value={termId}
              onChange={(e) => setTermId(e.target.value)}
            >
              <MenuItem value="" disabled>
                {t('sections.createDialog.termPlaceholder')}
              </MenuItem>
              {terms.map((t2) => (
                <MenuItem key={t2.id} value={t2.id}>
                  {t2.name}
                </MenuItem>
              ))}
            </TextField>

            {/* ── 4. Docente ── */}
            <TextField
              select
              label={t('sections.createDialog.faculty')}
              required
              fullWidth
              value={facultyId}
              onChange={(e) => setFacultyId(e.target.value)}
            >
              <MenuItem value="" disabled>
                {t('sections.createDialog.facultyPlaceholder')}
              </MenuItem>
              {faculty.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.firstName} {f.lastName}
                </MenuItem>
              ))}
            </TextField>

            {/* ── 5. Código de sección (único en el período; lo valida el servidor) ── */}
            <TextField
              label={t('sections.createDialog.sectionCode')}
              fullWidth
              value={sectionCode}
              onChange={(e) => setSectionCode(e.target.value)}
              helperText={t('sections.createDialog.sectionCodeHelper')}
            />

            {/* ── 6. Cupos máximos, o sin límite ── */}
            <Box>
              <FormControlLabel
                control={
                  <Switch checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} />
                }
                label={t('sections.createDialog.unlimited')}
              />
              {unlimited && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('sections.createDialog.unlimitedHelper')}
                </Typography>
              )}
            </Box>
            {unlimited ? null : (
              <TextField
                label={t('sections.createDialog.maxEnrollment')}
                type="number"
                required
                fullWidth
                value={maxEnrollment}
                onChange={(e) => setMaxEnrollment(e.target.value ? Number(e.target.value) : '')}
                slotProps={{ htmlInput: { min: 1 } }}
                helperText={t('sections.createDialog.maxEnrollmentHelper')}
              />
            )}

            {/* ── 7. Horario (opcional) ── */}
            <SectionScheduleEditor original={[]} value={schedule} onChange={setSchedule} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={isCreatingSection}>
            {t('sections.createDialog.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isCreatingSection || !isFormValid}
            sx={{ minWidth: 120 }}
          >
            {isCreatingSection
              ? t('sections.createDialog.creating')
              : t('sections.createDialog.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
